import { useState } from 'react';
import { ErrorEntity, Error as ErrorI } from '../../root/context';

interface UseIbanValidationOptions {
  apiRoute: string;
  apiRequest: (route: string, method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') => Promise<any>;
}

interface UseIbanValidationReturn {
  validateIban: (iban: string, bic: string) => Promise<{ iban: boolean; bic: boolean }>;
  isValidating: boolean;
  ibanValid: boolean | undefined;
  bicValid: boolean | undefined;
}

export const useIbanValidation = ({ apiRoute, apiRequest }: UseIbanValidationOptions): UseIbanValidationReturn => {
  const [isValidating, setIsValidating] = useState(false);
  const [ibanValid, setIbanValid] = useState<boolean | undefined>(undefined);
  const [bicValid, setBicValid] = useState<boolean | undefined>(undefined);
  const [lastValidatedIban, setLastValidatedIban] = useState<string>('');
  const [lastValidatedBic, setLastValidatedBic] = useState<string>('');

  const validateIban = async (iban: string, bic: string): Promise<{ iban: boolean; bic: boolean }> => {
    // Skip validation if values haven't changed
    if (iban === lastValidatedIban && bic === lastValidatedBic) {
      return { iban: ibanValid ?? false, bic: bicValid ?? false };
    }

    // Skip validation if already validating
    if (isValidating) {
      return { iban: false, bic: false };
    }

    setIsValidating(true);
    try {
      const response = await apiRequest(`${apiRoute}?iban=${iban}&bic=${bic}`, 'GET');

      // Update last validated values
      setLastValidatedIban(iban);
      setLastValidatedBic(bic);

      if (response.status === 200) {
        setIbanValid(true);
        setBicValid(true);
        return { iban: true, bic: true };
      } else if (response.status === 400 && response.error) {
        const errorEntity = (response.error as ErrorI).entity;
        if (errorEntity === ErrorEntity.InvalidBicForbidden) {
          setBicValid(false);
          setIbanValid(true);
          return { iban: true, bic: false };
        } else if (errorEntity === ErrorEntity.InvalidIbanForbidden) {
          setIbanValid(false);
          setBicValid(true);
          return { iban: false, bic: true };
        } else if (errorEntity === ErrorEntity.InvalidIbanAndBicForbidden) {
          setIbanValid(false);
          setBicValid(false);
          return { iban: false, bic: false };
        }
        return { iban: false, bic: false };
      }
      // Handle any other response status
      setIbanValid(false);
      setBicValid(false);
      return { iban: false, bic: false };
    } catch (error) {
      // Handle any unexpected errors
      setIbanValid(false);
      setBicValid(false);
      return { iban: false, bic: false };
    } finally {
      setIsValidating(false);
    }
  };

  return {
    validateIban,
    isValidating,
    ibanValid,
    bicValid
  };
};
